import { subDays } from 'date-fns';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { ChildEventProps } from './types';

export interface CalendarDayInfo {
    date: Date;
    events: Array<{}>;
}

export interface DatesRange {
    from: Date;
    to: Date;
}

type DatePeriod = Date | DatesRange;
export type DateModifiers = Array<DatePeriod>;

export const transformToDates = (dates: string[]) => {
    return dates.map((date) => {
        return new Date(date);
    });
};

const hasEventFollowingDay = (laterDate: Date, earlierDate: Date) =>
    differenceInCalendarDays(laterDate, earlierDate) === 1;

const hasMoreSessions = (laterDate: Date, earlierDate: Date) =>
    differenceInCalendarDays(laterDate, earlierDate) === 0;

const hasOnlyOneDayBetweenEvents = (laterDate: Date, earlierDate: Date) =>
    differenceInCalendarDays(laterDate, earlierDate) === 2;

const datesRangeModel = (from: Date, to: Date): DatesRange => ({
    from,
    to,
});

const getDatePeriod = (currentEventDate: Date, nextEventDate: Date) => {
    const nextDayToCurrentDate = addDays(currentEventDate, 1);
    const previousDayToNextDate = subDays(nextEventDate, 1);
    const parsedDatePeriod = hasOnlyOneDayBetweenEvents(
        nextEventDate,
        currentEventDate,
    )
        ? nextDayToCurrentDate
        : datesRangeModel(nextDayToCurrentDate, previousDayToNextDate);

    return parsedDatePeriod;
};

const parseDatePeriod = ({
    currentEventDate,
    nextEventDate,
}: {
    currentEventDate: Date;
    nextEventDate: Date;
}) =>
    hasEventFollowingDay(nextEventDate, currentEventDate) ||
    hasMoreSessions(nextEventDate, currentEventDate)
        ? null
        : getDatePeriod(currentEventDate, nextEventDate);

export const getDatesBetweenEvents = (eventDates: CalendarDayInfo[]) => {
    return eventDates.reduce<DateModifiers>(
        (datesBetweenEvents, currentEvent, index: number) => {
            const isLastEventRepetition = index === eventDates.length - 1;

            if (isLastEventRepetition) {
                return datesBetweenEvents;
            }

            const currentEventDate: Date = currentEvent.date;
            const nextEventDate: Date = eventDates[index + 1].date;
            const datePeriod = parseDatePeriod({
                currentEventDate,
                nextEventDate,
            }) as DatePeriod;

            return datePeriod
                ? ([...datesBetweenEvents, datePeriod] as DateModifiers)
                : datesBetweenEvents;
        },
        [],
    );
};

export const getChildAvailableEventsLocalStartDays = (
    childEvents: Array<ChildEventProps>,
) => childEvents.map((event) => event.start.local);
